import {
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy, ConnectedPosition } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { InfoTooltipComponent } from '../components/info-tooltip/info-tooltip.component';
import { InfoTooltipService } from '../services/info-tooltip.service';

/**
 * Directive to show an onboarding/info tooltip on an element
 * 
 * Usage:
 * <div infoTooltip="tts_home" 
 *      [tooltipContent]="'Touch this icon to translate to your language'"
 *      [tooltipPosition]="'above'">
 * </div>
 * 
 * Or with HTML template:
 * <div infoTooltip="custom_feature" 
 *      [tooltipTemplate]="myTemplate">
 * </div>
 * <ng-template #myTemplate>
 *   <p>Your custom <strong>HTML</strong> content</p>
 * </ng-template>
 */
@Directive({
    selector: '[infoTooltip]',
    standalone: true,
})
export class InfoTooltipDirective implements OnInit, OnChanges, OnDestroy {
    @Input('infoTooltip') tooltipId!: string; // Unique ID for tracking (e.g., 'tts_home', 'recovery_feature')
    @Input() tooltipLabel?: string; // Label for the tooltip (not used for tracking)
    @Input() tooltipContent?: string; // Simple text content
    @Input() tooltipTemplate?: TemplateRef<any>; // HTML template
    @Input() tooltipArrowPosition: 'top' | 'bottom' | 'left' | 'right' | 'auto' = 'auto';
    @Input() tooltipArrowOffsetValue: 'auto' | number | string | any = 'auto';
    @Input() autoShow = true; // Automatically show on init if not seen
    @Input() showBackdrop = false; // Darken background
    @Input() highlightElement = true; // Highlight the target element
    @Input() tooltipEnabled = true; // Gate showing the tooltip
    @Input() arrowXOffset?: number | string | null;
    @Input() arrowYOffset?: number | string | null;
    @Input() tooltipUserId?: string | number; // Optional user ID - makes tooltips user-specific

    /**
     * Adjust tooltip position with custom styles for desktop
     */
    @Input() adjustPosition?: Record<string, string | number>;
    /**
     * Adjust tooltip position with custom styles for mobile
     */
    @Input() adjustMobilePosition?: Record<string, string | number>;

    private overlay = inject(Overlay);
    private elementRef = inject(ElementRef);
    private viewContainerRef = inject(ViewContainerRef);
    private tooltipService = inject(InfoTooltipService);
    private overlayRef: OverlayRef | null = null;
    private highlightWrapper: HTMLElement | null = null;
    private highlightClone: HTMLElement | null = null;

    ngOnInit() {
        if (!!this.tooltipLabel) {
            this.tooltipId = this.tooltipLabel;
        }

        if (!this.tooltipId) {
            console.warn('InfoTooltipDirective: tooltipId is required');
            return;
        }

        if (this.autoShow) {
            this.tryShow();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['tooltipEnabled']) {
            if (this.tooltipEnabled === false) {
                this.hide();
            } else if (this.autoShow) {
                this.tryShow();
            }
        }
    }

    ngOnDestroy() {
        this.hide();
    }

    /**
     * Show the tooltip
     */
    show() {
        if (!this.tooltipEnabled) {
            return;
        }
        if (this.overlayRef) {
            return; // Already shown
        }

        // Create position strategy
        const positionStrategy = this.getPositionStrategy();

        // Create overlay config
        // Always create a backdrop to prevent interaction, but vary appearance
        const backdropClasses = this.showBackdrop
            ? 'info-tooltip-backdrop'
            : 'info-tooltip-backdrop-invisible';

        const overlayConfig = {
            positionStrategy,
            hasBackdrop: true, // Always block interaction
            backdropClass: backdropClasses,
            panelClass: 'info-tooltip-panel',
            scrollStrategy: this.overlay.scrollStrategies.reposition(),
        };

        // Create overlay
        this.overlayRef = this.overlay.create(overlayConfig);

        // Create component portal
        const tooltipPortal = new ComponentPortal(
            InfoTooltipComponent,
            this.viewContainerRef
        );

        // Attach component
        const componentRef = this.overlayRef.attach(tooltipPortal);

        // CDK measures the anchor element at attach() time, before the tooltip component itself
        // has rendered. Let the browser paint the tooltip first, then recalculate so CDK
        // knows the real overlay dimensions and can place it correctly.
        requestAnimationFrame(() => this.overlayRef?.updatePosition());

        const effectiveDirection = this.getEffectiveDirection();

        // Pass data to component
        componentRef.instance.content = this.tooltipContent;
        componentRef.instance.template = this.tooltipTemplate;
        componentRef.instance.direction = effectiveDirection;
        componentRef.instance.tooltipAlign = this.tooltipArrowOffsetValue;
        componentRef.instance.targetElement = this.elementRef.nativeElement;
        componentRef.instance.highlightElement = this.highlightElement;
        componentRef.instance.backdropEnabled = this.showBackdrop;
        componentRef.instance.dismissOnClick = !this.showBackdrop;
        componentRef.instance.showCloseButton = this.showBackdrop;
        componentRef.instance.arrowXOffset = this.arrowXOffset;
        componentRef.instance.arrowYOffset = this.arrowYOffset;

        this.applyAdjustments();

        // Handle close events
        componentRef.instance.close.subscribe(() => {
            this.onTooltipClose();
        });

        if (!this.showBackdrop) {
            this.overlayRef.backdropClick().subscribe(() => this.onTooltipClose());
        }

        // Add highlight clone only when backdrop is shown
        if (this.highlightElement && this.showBackdrop) {
            const rect = this.elementRef.nativeElement.getBoundingClientRect();

            // Create wrapper to provide subtle ring without altering clone size
            this.highlightWrapper = document.createElement('div');
            this.highlightWrapper.classList.add('info-tooltip-highlight');
            this.highlightWrapper.style.position = 'fixed';
            this.highlightWrapper.style.top = `${rect.top}px`;
            this.highlightWrapper.style.left = `${rect.left}px`;
            this.highlightWrapper.style.width = `${rect.width}px`;
            this.highlightWrapper.style.height = `${rect.height}px`;
            this.highlightWrapper.style.zIndex = '10001';
            this.highlightWrapper.style.pointerEvents = 'none';

            // Create a clone of the element (no extra classes or styles)
            this.highlightClone = this.elementRef.nativeElement.cloneNode(true) as HTMLElement;
            this.highlightClone.style.margin = '0';
            this.highlightClone.style.pointerEvents = 'none';

            // Hide the original element in a way that also removes it from rendering/accessibility
            this.elementRef.nativeElement.style.visibility = 'hidden';
            this.elementRef.nativeElement.style.opacity = '0';

            // Append clone to wrapper and wrapper to body
            this.highlightWrapper.appendChild(this.highlightClone);
            document.body.appendChild(this.highlightWrapper);
        }
    }

    /**
     * Hide the tooltip
     */
    hide() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }

        // Remove highlight clone
        if (this.highlightElement && this.showBackdrop) {
            if (this.highlightWrapper && this.highlightWrapper.parentNode) {
                this.highlightWrapper.parentNode.removeChild(this.highlightWrapper);
            }
            this.highlightWrapper = null;
            this.highlightClone = null;

            // Restore original element
            this.elementRef.nativeElement.style.opacity = '';
            this.elementRef.nativeElement.style.visibility = '';
        }
    }

    /**
     * Handle tooltip close
     */
    private onTooltipClose() {
        // Mark as seen (pass userId if available)
        this.tooltipService.markTooltipAsSeen(this.tooltipId, this.tooltipUserId);
        this.hide();
    }

    private tryShow() {
        if (!this.tooltipEnabled) {
            return;
        }
        this.tooltipService.hasSeenTooltip(this.tooltipId, this.tooltipUserId).then((seen) => {
            if (!seen) {
                this.showWhenStable();
            }
        });
    }

    /**
     * Poll the element's bounding rect until it has non-zero size and has stopped
     * moving between two consecutive checks. This replaces a fixed timeout and handles
     * slow devices, lazy-loaded components, and Ionic page-transition animations, all of
     * which can cause the element to be at the wrong position at any fixed delay.
     *
     * - minDelay:  guaranteed minimum wait so the page transition has started (ms)
     * - pollEvery: interval between stability checks after minDelay (ms)
     * - maxWait:   hard cap — show regardless after this many ms (ms)
     */
    private showWhenStable(minDelay = 600, pollEvery = 100, maxWait = 3000) {
        const el = this.elementRef.nativeElement as HTMLElement;
        const start = Date.now();
        let lastRect = el.getBoundingClientRect();

        const attempt = () => {
            const elapsed = Date.now() - start;
            const rect = el.getBoundingClientRect();

            const hasSize = rect.width > 0 && rect.height > 0;
            const isStable =
                Math.abs(rect.top - lastRect.top) < 0.5 &&
                Math.abs(rect.left - lastRect.left) < 0.5;

            lastRect = rect;

            if (elapsed >= minDelay && hasSize && isStable) {
                this.show();
            } else if (elapsed < maxWait) {
                setTimeout(attempt, pollEvery);
            } else {
                // Hard fallback: show after maxWait regardless
                this.show();
            }
        };

        setTimeout(attempt, minDelay);
    }

    /**
     * Get position strategy based on tooltipPosition
     */
    private getPositionStrategy(): PositionStrategy {
        const effectiveDirection = this.getEffectiveDirection();
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions(this.getPositions(effectiveDirection))
            .withFlexibleDimensions(true)
            .withPush(true);

        return positionStrategy;
    }

    /**
     * Get position configurations
     */
    private getPositions(direction: 'up' | 'down' | 'left' | 'right') {
        const positions: Record<'above' | 'below' | 'left' | 'right', ConnectedPosition[]> = {
            above: [
                {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'bottom',
                    offsetY: -8,
                }
            ],
            below: [
                {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'top',
                    offsetY: 8,
                },
            ],
            left: [
                {
                    originX: 'start',
                    originY: 'center',
                    overlayX: 'end',
                    overlayY: 'center',
                    offsetX: -8,
                },
            ],
            right: [
                {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'center',
                    offsetX: 8,
                },
            ],
        };
        const mapDirection: Record<'up' | 'down' | 'left' | 'right', 'above' | 'below' | 'left' | 'right'> = {
            up: 'above',
            down: 'below',
            left: 'left',
            right: 'right',
        };

        return positions[mapDirection[direction]];
    }

    private getEffectiveDirection(): 'up' | 'down' | 'left' | 'right' {
        // we need to determine direction based on element position .. so if manually set arrow direction up then tooltip should be down
        // if arrow is down then tooltip should be up etc

        if (this.tooltipArrowPosition !== 'auto') {
            switch (this.tooltipArrowPosition) {
                case 'top':
                    return 'down';
                case 'bottom':
                    return 'up';
                case 'left':
                    return 'right';
                case 'right':
                    return 'left';
            }
        }

        const rect = this.elementRef.nativeElement.getBoundingClientRect();
        const vw = window.innerWidth || document.documentElement.clientWidth;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        if (centerY > vh * 0.6) {
            return 'up';
        }
        if (centerY < vh * 0.4) {
            return 'down';
        }
        if (centerX > vw * 0.6) {
            return 'left';
        }
        return 'right';
    }

    private applyAdjustments() {
        if (!this.overlayRef) {
            return;
        }

        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        const styles = {
            ...(this.adjustPosition || {}),
            ...(isMobile ? this.adjustMobilePosition || {} : {}),
        };

        const overlayEl = this.overlayRef.overlayElement;
        Object.entries(styles).forEach(([key, value]) => {
            const normalizedValue = typeof value === 'number' ? `${value}px` : value;
            if (key.includes('-')) {
                overlayEl.style.setProperty(key, normalizedValue as string);
            } else {
                (overlayEl.style as any)[key] = normalizedValue;
            }
        });

        this.overlayRef.updatePosition();
    }
}
